/**
 * This file sets up internationalization (i18n) for the application with i18next.
 * @see {@link https://www.i18next.com/overview/configuration-options}
 */
/*
 * Translations are automatically loaded with i18next-loader and bundled.
 * Fetching translations over the network instead of compiling them into the bundle
 * allows internationalization workflows where an internationalization service
 * (https://locize.com/, https://crowdin.com/, https://lokalise.com/)
 * places the translations in a CDN.
 * But this requires using suspense in the application to prevent race conditions,
 * where translations are not yet loaded, e.g. with TanStack Router loader functions
 * that use i18n, which is a lot of additional effort.
 */
import i18n, { type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import resources from 'virtual:i18next-loader';
import { z } from 'zod';

import { createZodErrorMap } from '@vestigas/utils';

// Include all languages supported by the app here:
const supportedLanguages = ['de', 'en'] as const;

export type Locale = (typeof supportedLanguages)[number];

export const currentLocale = (): Locale => i18n.language as Locale;
// Include all namespaces used by the app here:
const translationNamespaces = [
  'admin-webapp',
  'aiModels',
  'aiProjects',
  'apiPollerSecrets',
  'clients',
  'common',
  'connections',
  'data',
  'dataExchange',
  'dataSyncPort',
  'deliveryNoteReparsingV2',
  'deliveryNotes',
  'filters',
  'forms',
  'ftpPoller',
  'internalUsers',
  'invoiceAiModels',
  'invoiceForwarding',
  'invoiceReprocessing',
  'overlayTemplates',
  'pdfTemplates',
  'pwa',
  'suppliers',
  'ui',
  'units',
  'universalFilters',
  'userFeatureFlags',
  'userSingleSignOn',
  'zodCustom',
] as const;
export const defaultNamespace = 'admin-webapp';

export type i18nNamespace = (typeof translationNamespaces)[number];

await i18n.use(initReactI18next).init({
  resources: resources as Resource,
  compatibilityJSON: 'v4', // Enable current pluralization, see https://www.i18next.com/translation-function/plurals
  debug: false,
  defaultNS: defaultNamespace,
  fallbackLng: ['de', 'en'],
  interpolation: {
    escapeValue: false, // XSS protection - not needed as React escapes by default.
  },
  lng: localStorage.getItem('language') ?? 'de', // Set the language. Don't set when using a language detector!
  // keySeparator: false, // Set to false if working with a flat JSON file.
  ns: translationNamespaces,
  react: { useSuspense: false },
  supportedLngs: supportedLanguages,
});

// Apply translations to Zod errors. The map is language-aware and reads the active
// i18next language at validation time, so a single registration serves all locales.
z.config(createZodErrorMap());

export { default } from 'i18next';
